import { useCallback, useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  saveOrEditPayrollEmpMapping,
  deletePayrollEmployee,
  getUnmatchedPayrollQueue,
  getLoadEmpPayrollDetails,
} from "../../../../../api";
import { buildUpdatePayrollEmployeeMappingPayload } from "../../../../../Payloads/EntryPayload";
import shortcutManager from "../../../../../Utilities/ShortcutManager";
import { parseHoursToDecimal } from "../../../../../HelperFunctions/PayrollEntryHelpers";

const normalizeVal = (val) => {
  if (val === null || val === undefined) return "";
  return String(val).trim();
};

const normalizeNum = (val) => {
  if (val === null || val === undefined || val === "") return "0.00";
  const num = parseFloat(String(val).replace(/,/g, ""));
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

export const arePayrollValuesEqual = (initF, initV, currF, currV) => {
  if (!initF || !currF) return true;

  // Header fields
  const headerFields = ["name", "department", "job", "checkNo", "profitCenter", "employeeId"];
  for (const f of headerFields) {
    if (normalizeVal(initF[f]) !== normalizeVal(currF[f])) {
      return false;
    }
  }

  // Account
  if (normalizeVal(initV?.account) !== normalizeVal(currV?.account)) {
    return false;
  }

  // Wages
  const isWageActive = (w) =>
    Boolean(
      normalizeVal(w.wageName) ||
      normalizeVal(w.mappedTo) ||
      normalizeVal(w.coa) ||
      parseHoursToDecimal(w.hrs) > 0 ||
      Number(w.gross || 0) > 0
    );
  const initWages = (initV?.wage || []).filter(isWageActive);
  const currWages = (currV?.wage || []).filter(isWageActive);
  if (initWages.length !== currWages.length) return false;
  for (let i = 0; i < initWages.length; i++) {
    const iw = initWages[i];
    const cw = currWages[i];
    if (
      normalizeVal(iw.wageName) !== normalizeVal(cw.wageName) ||
      normalizeVal(iw.mappedTo) !== normalizeVal(cw.mappedTo) ||
      normalizeVal(iw.coa) !== normalizeVal(cw.coa) ||
      parseHoursToDecimal(iw.hrs) !== parseHoursToDecimal(cw.hrs) ||
      normalizeNum(iw.rate) !== normalizeNum(cw.rate) ||
      normalizeNum(iw.gross) !== normalizeNum(cw.gross)
    ) {
      return false;
    }
  }

  // Additions & Deductions
  const isAddDedActive = (a) =>
    Boolean(
      normalizeVal(a.item) ||
      normalizeVal(a.mappedTo) ||
      normalizeVal(a.coa) ||
      Number(a.amount || 0) > 0
    );
  const initAdd = (initV?.addAndDeductions || []).filter(isAddDedActive);
  const currAdd = (currV?.addAndDeductions || []).filter(isAddDedActive);
  if (initAdd.length !== currAdd.length) return false;
  for (let i = 0; i < initAdd.length; i++) {
    const ia = initAdd[i];
    const ca = currAdd[i];
    if (
      normalizeVal(ia.addDeduction) !== normalizeVal(ca.addDeduction) ||
      normalizeVal(ia.item) !== normalizeVal(ca.item) ||
      normalizeVal(ia.mappedTo) !== normalizeVal(ca.mappedTo) ||
      normalizeVal(ia.coa) !== normalizeVal(ca.coa) ||
      normalizeNum(ia.amount) !== normalizeNum(ca.amount)
    ) {
      return false;
    }
  }

  // Taxes
  const isTaxActive = (t) =>
    Boolean(
      normalizeVal(t.item) ||
      normalizeVal(t.mappedTo) ||
      normalizeVal(t.coa) ||
      normalizeVal(t.expenseCoa) ||
      Number(t.amount || 0) > 0
    );
  const initTax = (initV?.eeAndErTaxes || []).filter(isTaxActive);
  const currTax = (currV?.eeAndErTaxes || []).filter(isTaxActive);
  if (initTax.length !== currTax.length) return false;
  for (let i = 0; i < initTax.length; i++) {
    const it = initTax[i];
    const ct = currTax[i];
    if (
      normalizeVal(it.taxType) !== normalizeVal(ct.taxType) ||
      normalizeVal(it.item) !== normalizeVal(ct.item) ||
      normalizeVal(it.mappedTo) !== normalizeVal(ct.mappedTo) ||
      normalizeVal(it.coa) !== normalizeVal(ct.coa) ||
      normalizeVal(it.expenseCoa) !== normalizeVal(ct.expenseCoa) ||
      normalizeNum(it.amount) !== normalizeNum(ct.amount)
    ) {
      return false;
    }
  }

  return true;
};

export const useEmployeePayrollMappingActions = ({
  row,
  values,
  formValues,
  payrollId,
  corporationId,
  isCompletedRecord = false,
  isVoid,
  voidDate,
  voidDatePref,
  comments,
  empPayrollDetails,
  employeeTotalGross,
  payPeriodTotals,
  permissions,
  popupID,
  setStep,
  setSelectedRow,
  setFieldValue,
  apiRatesRef,
  originalFormValuesRef,
  initialPayrollStateRef,
  hasCalledUnmatchedQueueRef,
  resetPayrollTableValues,
  validateForm,
  getCleanedValues,
  showErrorSnackbar,
  showSuccessSnackbar,
  showDeleteConfirm,
  showCancelConfirm,
  alertPopupOpen = false,
}) => {
  const [updatingPayroll, setUpdatingPayroll] = useState(false);
  const [navigatingEmployee, setNavigatingEmployee] = useState(false);
  const [rateChangeConfirm, setRateChangeConfirm] = useState({ open: false, onYes: null, onNo: null });

  // 1. Advance to Next Employee (Used by "Update & Next")
  const handleNextEmployee = useCallback(async () => {
    resetPayrollTableValues();
    setNavigatingEmployee(true);
    try {
      const pageNumber = row?.pageNum || formValues?.pageNum || values?.pageNum || row?.raw?.PageNum || 1;
      const response = await getLoadEmpPayrollDetails(
        payrollId ? payrollId : values?.payRollID,
        pageNumber
      );
      const details = response?.PayEmpDetails || [];
      const currentId = values.payrollEmpId || row?.raw?.PayrollEmpID || row?.raw?.PayrollEmpId;

      const currentIndex = details.findIndex(
        (d) => (d.PayrollEmpID || d.PayrollEmpId) === currentId
      );

      const isMatchedStatus = (status) => {
        const normalized = String(status || "").trim().toLowerCase();
        return normalized === "completed" || normalized === "matched";
      };
      const isAttention = (item) => !isMatchedStatus(item.MappingStatus);

      const hasAttentionItems = details.some(isAttention);
      let nextIndex = -1;

      if (hasAttentionItems) {
        // Find next unmapped/attention item
        for (let i = 1; i < details.length; i++) {
          const checkIndex = (currentIndex + i) % details.length;
          if (isAttention(details[checkIndex])) {
            nextIndex = checkIndex;
            break;
          }
        }
      } else {
        // All are completed, find next completed item
        if (details.length > 1) {
          nextIndex = (currentIndex + 1) % details.length;
        }
      }

      if (nextIndex !== -1) {
        const nextItem = details[nextIndex];
        const nextRow = {
          name: nextItem.EmployeeName,
          department: nextItem.Department,
          jobTitle: nextItem.JobTitle,
          profitCenter: nextItem.ProfitCenter || nextItem.PCName || "",
          hrs: nextItem.TotalHours ?? nextItem.Hours ?? 0,
          grossPay: nextItem.Wages,
          additions: nextItem.Additions,
          deductions: nextItem.Deductions,
          eeTaxes: nextItem.EmployeeTaxes,
          netPay: nextItem.NetPay,
          erTaxes: nextItem.EmployerTaxes,
          view: isAttention(nextItem) ? "Unmapped" : "Completed",
          statusText: isAttention(nextItem) ? "Unmapped" : "Completed",
          raw: nextItem,
        };
        setSelectedRow(nextRow);
      } else {
        showSuccessSnackbar("All employees matched successfully.");
        setStep(2);
      }
    } catch (error) {
      console.error("Failed to load next employee:", error);
    } finally {
      setNavigatingEmployee(false);
    }
  }, [
    resetPayrollTableValues,
    payrollId,
    values?.payRollID,
    values?.payrollEmpId,
    values?.pageNum,
    formValues?.pageNum,
    row,
    setSelectedRow,
    showSuccessSnackbar,
    setStep,
  ]);

  // 2. Execute Update (Save/Edit API call)
  const executeHandleUpdate = useCallback(
    async (andClose, isEmpPayrollDetail, cleanedValues, payrollEmpId) => {
      const isVoidSectionVisible =
        (row?.statusText === "Matched" || row?.view === "Matched") &&
        (!formValues.isVoidStamp || voidDatePref);
      let finalIsVoid = !isVoidSectionVisible && formValues.isVoidStamp ? false : isVoid;
      if (row?.statusText === "Unmapped" || row?.view === "Unmapped") {
        finalIsVoid = false;
      }

      if (finalIsVoid && voidDatePref) {
        const pDate = values.payDate ? dayjs(values.payDate) : null;
        const vDate = voidDate ? dayjs(voidDate) : null;
        if (vDate && pDate && vDate.isBefore(pDate, "day")) {
          showErrorSnackbar("Void Date cannot be less than Pay Date.");
          return;
        }
      }

      setUpdatingPayroll(true);

      const payload = buildUpdatePayrollEmployeeMappingPayload(
        { ...formValues, bankAccountID: values.account },
        cleanedValues,
        payrollId,
        corporationId,
        {
          ...row,
          raw: {
            ...row?.raw,
            PayrollEmpID: payrollEmpId || values.payrollEmpId,
          },
        },
        finalIsVoid,
        voidDatePref ? voidDate : null,
        voidDatePref ? comments : null,
        empPayrollDetails,
        voidDatePref,
        employeeTotalGross,
        payPeriodTotals
      );
      payload.IsEmpPayrollDetail = isEmpPayrollDetail;

      try {
        const response = await saveOrEditPayrollEmpMapping(payload);
        if (response?.StatusCode === 200 || response?.Status === "Success") {
          showSuccessSnackbar("Employee payroll updated successfully.");
          resetPayrollTableValues();
          if (andClose) {
            const isCompletedState = Boolean(
              isCompletedRecord ||
              String(row?.statusText || "").trim().toLowerCase() === "completed" ||
              String(row?.statusText || "").trim().toLowerCase() === "matched" ||
              String(row?.view || "").trim().toLowerCase() === "completed" ||
              String(row?.view || "").trim().toLowerCase() === "matched" ||
              String(row?.raw?.MappingStatus || "").trim().toLowerCase() === "completed" ||
              String(row?.raw?.MappingStatus || "").trim().toLowerCase() === "matched"
            );

            const isUnmapped =
              (String(row?.statusText || "").trim().toLowerCase() === "unmapped" ||
                String(row?.view || "").trim().toLowerCase() === "unmapped") &&
              !isCompletedState;

            const alreadyCalled = Boolean(
              values?.unmatchedQueueCalled || hasCalledUnmatchedQueueRef?.current
            );

            if (values.importPayrollType === 2 && isUnmapped && !isCompletedState && !alreadyCalled) {
              if (hasCalledUnmatchedQueueRef) hasCalledUnmatchedQueueRef.current = true;
              setFieldValue("unmatchedQueueCalled", true);
              try {
                await getUnmatchedPayrollQueue({
                  CurrentPayrollEmployeeId:
                    response?.PayrollEmpID ||
                    response?.ID ||
                    row?.raw?.PayrollEmpID ||
                    row?.raw?.PayrollEmpId ||
                    "",
                  SourceCorpId: corporationId || values?.corporationId || values?.corpId || "",
                });
              } catch (e) {
                console.error("Error calling unmatched payroll queue", e);
              }
            }
            if (popupID) {
              window.parent.postMessage(`popuphide|${popupID}`, "*");
            }
            setStep(2);
          } else {
            await handleNextEmployee();
          }
        } else {
          throw new Error(response?.Status || "Failed to update.");
        }
      } catch (error) {
        showErrorSnackbar(error.message || "An error occurred during update.");
      } finally {
        setUpdatingPayroll(false);
      }
    },
    [
      row,
      formValues,
      voidDatePref,
      isVoid,
      values,
      voidDate,
      showErrorSnackbar,
      payrollId,
      corporationId,
      isCompletedRecord,
      comments,
      empPayrollDetails,
      employeeTotalGross,
      payPeriodTotals,
      showSuccessSnackbar,
      resetPayrollTableValues,
      hasCalledUnmatchedQueueRef,
      setFieldValue,
      popupID,
      setStep,
      handleNextEmployee,
    ]
  );

  // 3. Handle Update Action (Supports both "Update & Next" and "Update & Close")
  const handleUpdate = useCallback(
    async (andClose = false) => {
      if (!validateForm()) {
        return;
      }

      const cleanedValues = getCleanedValues(values);
      const isRateChanged = (cleanedValues.wage || []).some((w) => {
        const apiRate = apiRatesRef?.current?.[w.id] || "0.00";
        return Number(w.rate) !== Number(apiRate);
      });

      const isDeptChanged = formValues.department !== originalFormValuesRef?.current?.department;
      const isJobChanged = formValues.job !== originalFormValuesRef?.current?.job;
      const isPCChanged = formValues.profitCenter !== originalFormValuesRef?.current?.profitCenter;

      if (isRateChanged || isDeptChanged || isJobChanged || isPCChanged) {
        setRateChangeConfirm({
          open: true,
          onYes: () => {
            setRateChangeConfirm({ open: false, onYes: null, onNo: null });
            executeHandleUpdate(andClose, true, cleanedValues);
          },
          onNo: () => {
            setRateChangeConfirm({ open: false, onYes: null, onNo: null });
            executeHandleUpdate(andClose, false, cleanedValues);
          },
        });
      } else {
        executeHandleUpdate(andClose, false, cleanedValues, values.payrollEmpId);
      }
    },
    [
      validateForm,
      getCleanedValues,
      values,
      apiRatesRef,
      formValues.department,
      formValues.job,
      formValues.profitCenter,
      originalFormValuesRef,
      executeHandleUpdate,
    ]
  );

  // 4. Handle Delete Action
  const handleDelete = useCallback(() => {
    if (permissions && !permissions.canDelete) {
      showErrorSnackbar("You don't have permission to delete this employee mapping.");
      return;
    }

    const payrollEmpId = values.payrollEmpId || row?.raw?.PayrollEmpID || row?.raw?.PayrollEmpId;
    if (!payrollEmpId) {
      showErrorSnackbar("No Employee mapping found to delete.");
      return;
    }

    showDeleteConfirm("Are you sure you want to delete this employee mapping?", async () => {
      setUpdatingPayroll(true);
      try {
        const response = await deletePayrollEmployee(payrollEmpId);
        if (response?.StatusCode === 200 || response?.Status === "Success") {
          showSuccessSnackbar("Employee mapping deleted successfully.");
          resetPayrollTableValues();
          if (popupID) {
            window.parent.postMessage(`popuphide|${popupID}`, "*");
          }
          setStep(2);
        } else {
          showErrorSnackbar(response?.Message || response?.Status || "Failed to delete employee mapping.");
        }
      } catch (error) {
        console.error("Error deleting employee:", error);
        showErrorSnackbar(error.message || "Failed to delete employee mapping.");
      } finally {
        setUpdatingPayroll(false);
      }
    });
  }, [
    permissions,
    values.payrollEmpId,
    row,
    showErrorSnackbar,
    showDeleteConfirm,
    showSuccessSnackbar,
    resetPayrollTableValues,
    popupID,
    setStep,
  ]);

  // 5. Handle Cancel Action
  const handleCancel = useCallback(() => {
    const isDirty = initialPayrollStateRef?.current
      ? !arePayrollValuesEqual(
        initialPayrollStateRef.current.formValues,
        initialPayrollStateRef.current.values,
        formValues,
        values
      )
      : false;

    if (isDirty) {
      showCancelConfirm("Changes have been made. Are you sure you want to cancel?", () => {
        resetPayrollTableValues();
        if (popupID) {
          window.parent.postMessage(`popuphide|${popupID}`, "*");
        }
        setStep(2);
      });
    } else {
      resetPayrollTableValues();
      if (popupID) {
        window.parent.postMessage(`popuphide|${popupID}`, "*");
      }
      setStep(2);
    }
  }, [
    initialPayrollStateRef,
    formValues,
    values,
    showCancelConfirm,
    resetPayrollTableValues,
    popupID,
    setStep,
  ]);

  // Register Keyboard Shortcuts
  useEffect(() => {
    if (alertPopupOpen) return;

    shortcutManager.addShortcut("alt+n", () => {
      handleUpdate(false);
    });
    shortcutManager.addShortcut("alt+p", () => {
      handleUpdate(true);
    });
    shortcutManager.addShortcut("alt+d", () => {
      handleDelete();
    });
    shortcutManager.addShortcut("alt+c", () => {
      handleCancel();
    });

    return () => {
      shortcutManager.removeShortcut("alt+n");
      shortcutManager.removeShortcut("alt+p");
      shortcutManager.removeShortcut("alt+d");
      shortcutManager.removeShortcut("alt+c");
    };
  }, [alertPopupOpen, handleUpdate, handleCancel, handleDelete]);

  return {
    handleUpdate,
    executeHandleUpdate,
    handleDelete,
    handleCancel,
    handleNextEmployee,
    updatingPayroll,
    setUpdatingPayroll,
    navigatingEmployee,
    rateChangeConfirm,
    setRateChangeConfirm,
  };
};
